import { useCallback } from 'react';

import { MutateOptions, QueryClient, useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import { toastClose, toastError } from '@telialt/common-toast/src';
import { AxiosCartError, Cart, CartErrorTypes, KnownReason } from '@telialt/models/src/EshopModel';
import { datadogLogger } from '@telialt/utils/src/datadog/datadogLogger';
import { DDComponent } from '@telialt/utils/src/datadog/setup';
import { toCartError, updateCartSession, updateStartOverLink } from '@telialt/utils/src/eshopUtils';

import { cancelCartQuery, updateCart } from '../../helpers/cartQueryUtils';
import { ESHOP_QUERY_KEY } from '../../model';

/** @name ERROR_MESSAGE
 * better to hardcode, than rely on useTranslations, which can be async because of api call.
 * if a hook (which extended useMutationWithCartRefresh or reused resetToPreviousCartState) is called earlier,
 * race conditions happen & we get "Error('useInitializeTranslations should be called before using useTranslations')" */
const ERROR_MESSAGE = 'Atsiprašome, dėl techninių kliūčių įvyko klaida. Pabandykite pakartoti paskutinį veiksmą.';

export const resetToPreviousCartState =
    (queryClient: QueryClient, errorMessage: string = ERROR_MESSAGE) =>
    async (error: AxiosCartError, _variables: unknown, cartCache: Cart) => {
        updateCart(queryClient, cartCache);
        const datadogLogs = datadogLogger();
        datadogLogs.error('Cart mutation error resetToPreviousCartState', {
            error,
            component: DDComponent.EshopProductCore,
        });
        toastError(errorMessage, {
            toastId: 'resetToPreviousCartStateToast',
            onClick: () => toastClose('resetToPreviousCartStateToast'),
        });

        const cartError = toCartError(error);
        if (cartError.type === CartErrorTypes.CartError && cartError.reason === KnownReason.NotFound) {
            await queryClient.invalidateQueries({ queryKey: [ESHOP_QUERY_KEY.CART] });
            await queryClient.resetQueries({ queryKey: [ESHOP_QUERY_KEY.CART] });
        }
    };

export const useMutationWithCartRefresh = <TVariables = void>(
    action: (args: TVariables) => Promise<Cart>,
    config?: UseMutationOptions<Cart, AxiosCartError, TVariables>,
) => {
    const queryClient = useQueryClient();

    const { mutate, mutateAsync, ...mutationVars } = useMutation({
        mutationFn: (args: TVariables) => action(args),
        onMutate: () => cancelCartQuery(queryClient),
        onError: async (error) => {
            const datadogLogs = datadogLogger();
            datadogLogs.error('Cart mutation error', { error, component: DDComponent.EshopProductCore });
            toastError(ERROR_MESSAGE, {
                toastId: 'useMutationWithCartRefreshToast',
                onClick: () => toastClose('useMutationWithCartRefreshToast'),
            });

            const cartError = toCartError(error);
            if (cartError.type === CartErrorTypes.CartError && cartError.reason === KnownReason.NotFound) {
                await queryClient.invalidateQueries({ queryKey: [ESHOP_QUERY_KEY.CART] });
                await queryClient.resetQueries({ queryKey: [ESHOP_QUERY_KEY.CART] });
            }
        },
        ...config,
    });

    const onSuccess = useCallback(
        (cartMutationResponse: Cart) => {
            updateCart(queryClient, cartMutationResponse);
            updateCartSession(cartMutationResponse);
            updateStartOverLink(cartMutationResponse);
        },
        [queryClient],
    );

    // callbacks on mutate function are only called when component is mounted. Wrapping mutate function to include onSuccess callback
    const wrappedMutate = useCallback(
        (args?: TVariables, options?: MutateOptions<Cart, unknown, TVariables>) =>
            mutate(args, {
                ...options,
                onSuccess,
            }),
        [mutate, onSuccess],
    );

    const wrappedMutateAsync = useCallback(
        (args?: TVariables, options?: MutateOptions<Cart, unknown, TVariables>) =>
            mutateAsync(args, {
                ...options,
                onSuccess,
            }),
        [mutateAsync, onSuccess],
    );

    return {
        ...mutationVars,
        mutate: wrappedMutate,
        mutateAsync: wrappedMutateAsync,
        error: mutationVars.error ? toCartError(mutationVars.error) : null,
    };
};
